import { getMaxLeverage } from '@/features/trade/order/utils/math';
import useViewport from '@/hooks/use-viewport';
import { abbrFromSym } from '@/utils/token-symbol';
import { formatBigInt } from '@/utils/value-format';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getAllMarketStoreStates } from '../store/use-markets-store';

const ANNOUNCED_MARKETS = ['SKHY-USD-PERP'];
const STORAGE_KEY = 'listings-toast-dismissed';

export default function ListingsToast() {
  const hasHydrated = useRef(false);
  const { t, i18n } = useTranslation();
  const currentLanguage = useRef(i18n.language);
  const toastIdRef = useRef<number | string | null>(null);

  const viewport = useViewport();

  const maxLeverage = useMemo(() => {
    const markets = getAllMarketStoreStates();
    return ANNOUNCED_MARKETS.reduce((prev, symbol) => {
      if (!(symbol in markets)) return prev;
      const { marketSpec } = markets[symbol];
      const maxLeverage = getMaxLeverage(marketSpec.initMarginRatio);
      if (!maxLeverage) return prev;
      if (maxLeverage.bigint > prev) return maxLeverage.bigint;
      return prev;
    }, 100n);
  }, []);

  const createAnnouncementToast = useCallback(() => {
    const markets = getAllMarketStoreStates();
    const symbols = ANNOUNCED_MARKETS.filter((symbol) => symbol in markets);
    if (symbols.length === 0) return;
    return toast.info(
      <div className="flex flex-col gap-1">
        <div className="mb-1 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Info size={12} className="inline-block" />{' '}
          {t('announcementToast.newPair')}
        </div>
        <p className="line-clamp-1">
          <strong>{t('announcementToast.newListing')}</strong>{' '}
          {symbols
            .map((symbol) => markets[symbol].marketSpec.displayName)
            .join(', ')}
        </p>
        <p className="text-vestgrey-500">
          {t('announcementToast.vestListed')}{' '}
          {symbols.map((symbol, i, arr) => (
            <>
              <button
                className="inline-block font-semibold text-foreground underline"
                onClick={() => {
                  const navigate = new CustomEvent('app:navigate', {
                    detail: { pathname: `/trade/${symbol}` },
                  });
                  window.dispatchEvent(navigate);
                }}
              >
                {abbrFromSym(symbol)}
              </button>
              {/* {i === arr.length - 2 ? ' and ' : i < arr.length - 1 && ', '} */}
              {i < arr.length - 1 && ', '}
            </>
          ))}{' '}
          {t('announcementToast.perpetualContract')}{' '}
          {formatBigInt(maxLeverage, 2, {
            style: 'leverage',
          })}
          {t('announcementToast.leverage')}
        </p>
      </div>,
      {
        position: viewport === 'sm' ? 'top-right' : 'bottom-right',
        duration: Infinity,
        onDismiss: () => {
          localStorage.setItem(STORAGE_KEY, ANNOUNCED_MARKETS.join(','));
          toastIdRef.current = null;
        },
      },
    );
  }, [maxLeverage, viewport]);

  useEffect(() => {
    if (currentLanguage.current !== i18n.language && toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      currentLanguage.current = i18n.language;
      toastIdRef.current = createAnnouncementToast() ?? null;
      return;
    }

    if (currentLanguage.current !== i18n.language) {
      currentLanguage.current = i18n.language;
    }

    if (hasHydrated.current === true) return;

    if (localStorage.getItem(STORAGE_KEY) === ANNOUNCED_MARKETS.join(','))
      return;

    hasHydrated.current = true;
    toastIdRef.current = createAnnouncementToast() ?? null;

    return () => {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [i18n.language, createAnnouncementToast]);

  return <></>;
}
